import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMyBarExamSubmission } from "../api/barExam";

const formatScore = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return (Math.round((numericValue + Number.EPSILON) * 10) / 10).toFixed(1);
};

const ResultCard = ({ label, value, suffix = "" }) => (
  <Box p="5" rounded="md" borderWidth="1px" bg="white" textAlign="center">
    <Text color="gray.500" fontSize="sm" mb="2">
      {label}
    </Text>
    <Text color="blue.600" fontSize="2xl" fontWeight="bold">
      {value ?? "-"}
      {value !== null && value !== undefined ? suffix : ""}
    </Text>
  </Box>
);

const BarExamResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateResult = location.state?.result;
  const stateExamId =
    location.state?.examId ||
    stateResult?.exam_id ||
    stateResult?.submission?.exam_id;
  const [result, setResult] = useState(stateResult || null);
  const [examId] = useState(
    stateExamId || sessionStorage.getItem("last_bar_exam_id"),
  );
  const [loading, setLoading] = useState(!stateResult && Boolean(examId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (stateExamId) {
      sessionStorage.setItem("last_bar_exam_id", stateExamId);
    }

    if (result || !examId) {
      return;
    }

    const loadResult = async () => {
      try {
        const data = await fetchMyBarExamSubmission(examId);

        if (!data.exists) {
          setError("제출한 시험 결과가 없습니다.");
          return;
        }

        setResult(data);
      } catch (error) {
        console.error("Error fetching my bar exam result:", error);
        setError(
          error.data?.detail || "시험 결과를 불러오지 못했습니다.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadResult();
  }, [examId, result, stateExamId]);

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        minH="100vh"
        bgGradient="linear(to-r, blue.50, blue.100)"
      >
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (!result) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="100vh"
        bgGradient="linear(to-r, blue.50, blue.100)"
        p={4}
      >
        <Alert status="warning" rounded="md" maxW="600px" mb="4">
          <AlertIcon />
          {error || "표시할 시험 결과가 없습니다."}
        </Alert>
        <Button colorScheme="blue" onClick={() => navigate("/bar-exam")}>
          시험 목록으로
        </Button>
      </Flex>
    );
  }

  const { submission, ranking } = result;
  const hasEditStatus = typeof submission.can_edit === "boolean";
  const canEdit = Boolean(submission.can_edit && examId);

  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      bgGradient="linear(to-r, blue.50, blue.100)"
      p={{ base: 4, md: 8 }}
    >
      <Box width="full" maxW="900px">
        <Heading as="h1" mb="8" textAlign="center">
          변호사시험 모의고사 결과
        </Heading>

        {hasEditStatus && (
          <Alert
            status={submission.can_edit ? "info" : "warning"}
            rounded="md"
            mb="6"
          >
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">
                {submission.can_edit
                  ? "답안을 1회 수정할 수 있습니다."
                  : "수정 가능 횟수를 모두 사용했습니다."}
              </Text>
              <Text fontSize="sm">
                수정 횟수 {submission.edit_count}/{submission.max_edit_count}
                {submission.is_locked && " · 현재 답안 잠금 상태"}
              </Text>
            </Box>
          </Alert>
        )}

        <Box p={{ base: 4, md: 6 }} rounded="md" shadow="md" bg="white" mb="6">
          <Heading as="h2" size="md" mb="4">
            점수
          </Heading>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing="4">
            <ResultCard
              label="공법"
              value={formatScore(submission.public_law_score)}
              suffix="점"
            />
            <ResultCard
              label="형사법"
              value={formatScore(submission.criminal_law_score)}
              suffix="점"
            />
            <ResultCard
              label="민사법"
              value={formatScore(submission.civil_law_score)}
              suffix="점"
            />
            <ResultCard
              label="총점"
              value={formatScore(submission.total_score)}
              suffix="점"
            />
          </SimpleGrid>
        </Box>

        <Box p={{ base: 4, md: 6 }} rounded="md" shadow="md" bg="white" mb="6">
          <Heading as="h2" size="md" mb="4">
            답안 현황
          </Heading>
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="4">
            <ResultCard
              label="정답"
              value={submission.correct_count}
              suffix="개"
            />
            <ResultCard
              label="오답"
              value={submission.wrong_count}
              suffix="개"
            />
            <ResultCard
              label="미응답"
              value={submission.blank_count}
              suffix="개"
            />
          </SimpleGrid>
        </Box>

        <Box p={{ base: 4, md: 6 }} rounded="md" shadow="md" bg="white" mb="6">
          <Heading as="h2" size="md" mb="4">
            예상 순위
          </Heading>
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="4">
            <ResultCard
              label="예상 등수"
              value={ranking.estimated_rank}
              suffix="위"
            />
            <ResultCard label="백분위" value={ranking.percentile} suffix="%" />
            <ResultCard
              label="전체 응시자"
              value={ranking.submitter_count}
              suffix="명"
            />
          </SimpleGrid>
        </Box>

        <Flex gap="3" direction={{ base: "column", sm: "row" }}>
          {canEdit && (
            <Button
              colorScheme="orange"
              width="full"
              onClick={() => navigate(`/bar-exam/${examId}/answer`)}
            >
              답안 수정하기
            </Button>
          )}
          <Button
            colorScheme="blue"
            width="full"
            isDisabled={!examId}
            onClick={() => navigate(`/bar-exam/${examId}/statistics`)}
          >
            전체 통계 보기
          </Button>
          <Button
            colorScheme="gray"
            variant="outline"
            width="full"
            onClick={() => navigate("/bar-exam")}
          >
            시험 목록으로
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default BarExamResult;
